import { mat4, vec3 } from 'gl-matrix';

import { DefaultCamera } from './DefaultCamera';
import { PropertyAnimation } from '../animation';
import { ViewPort } from '../context/ViewPort';
import { toRadian } from '../math';

export class AttachedCamera extends DefaultCamera {
  public animatedProperties?: PropertyAnimation;
  private lastLocation: vec3;
  private lastRotation: number;
  private _rotation: number;
  private _tilt: number;
  private _cameraPosition: vec3;
  private target?: CameraTargetType;

  public distance: number;
  public yOffset: number;
  public targetYOffset: number;
  public freeOrbit: boolean;
  /** Max camera world-Y set by PlayerController each frame; null = unclamped. */
  public maxCameraY: number | null = null;
  private _smoothedTargetY: number = 0;
  private _smoothedTargetYInitialized: boolean = false;

  constructor(viewPort: ViewPort) {
    super(viewPort);

    this.lastLocation = vec3.create();
    this._cameraPosition = vec3.create();
    this.lastRotation = 0;
    this._rotation = 0;
    this._tilt = 0;
    this.freeOrbit = false;

    this.distance = 4;
    this.yOffset = 1.6;
    this.targetYOffset = 0;
  }

  public attach(target: CameraTargetType): void {
    this.target = target;
  }

  public get rotation(): number {
    return this._rotation;
  }

  public set rotation(value: number) {
    this._rotation = value;
    this.invalidatedModelView = true;
  }

  public get tilt(): number {
    return this._tilt;
  }

  public set tilt(value: number) {
    this._tilt = Math.max(-80, Math.min(80, value));
    this.invalidatedModelView = true;
  }

  protected applyModelView(matrix: mat4): void {
    if (!this.target) {
      return;
    }

    const lookAt = vec3.fromValues(
      this.target.position[0] as number,
      this._smoothedTargetY + this.targetYOffset,
      this.target.position[2] as number,
    );

    mat4.lookAt(
      matrix,
      this.cameraPosition,
      lookAt,
      vec3.fromValues(0, 1, 0), // y is up
    );
  }

  public get cameraPosition(): vec3 {
    let rotation = this._rotation - 180;
    let tiltRad = toRadian(this._tilt);
    let horizontalDist = this.distance * Math.cos(tiltRad);
    let verticalOffset = this.yOffset + this.distance * Math.sin(tiltRad);

    this._cameraPosition[0] =
      this.target!.position[0] + horizontalDist * Math.sin(toRadian(rotation));
    this._cameraPosition[1] = this._smoothedTargetY + this.targetYOffset + verticalOffset;
    this._cameraPosition[2] =
      this.target!.position[2] + horizontalDist * Math.cos(toRadian(rotation));

    if (this.maxCameraY !== null && this._cameraPosition[1] > this.maxCameraY) {
      this._cameraPosition[1] = this.maxCameraY;
    }

    return this._cameraPosition;
  }

  public get targetPosition(): vec3 | number[] {
    return this.target!.position;
  }

  public prepareFrame(): void {
    if (!this.target) {
      return;
    }

    this.prepareTemporalFrame();

    if (this.animatedProperties) {
      this.animatedProperties.step();
    }

    if (!this.freeOrbit) {
      // Track entity rotation (default behavior)
      if (Math.abs(this.lastRotation - this.target.rotation) > 0.0001) {
        this.invalidatedModelView = true;

        this.lastRotation = this.target.rotation;
        this.rotate(this.lastRotation);
      }
    }

    const targetY = this.target.position[1] as number;
    if (!this._smoothedTargetYInitialized) {
      this._smoothedTargetY = targetY;
      this._smoothedTargetYInitialized = true;
    } else {
      this._smoothedTargetY += (targetY - this._smoothedTargetY) * 0.15;
    }

    if (vec3.dist(this.lastLocation, this.target.position) > 0.0001) {
      this.invalidatedModelView = true;
      vec3.copy(this.lastLocation, this.target.position);
    }
  }

  private rotate(newRotation: number) {
    this.animatedProperties = new PropertyAnimation(this, () => {});
    this.animatedProperties.animate([{ property: '_rotation', value: newRotation, speed: 5 }]);
  }
}

type CameraTargetType = {
  position: vec3 | number[];
  rotation: number;
};
